import { defineStore } from 'pinia';
import { ref } from 'vue';

import { crossbeamApi } from '@/api';
import { captureException } from '@/errors';
import { initStore } from '@/stores/store-utils';
import {
  InboundShareRequest,
  OutboundShareRequest,
} from '@/types/data_share_requests';

export const useDataRequestsStore = defineStore('DataRequests', () => {
  const inboundShareRequests = ref<InboundShareRequest[]>([]);
  const outboundShareRequests = ref<OutboundShareRequest[]>([]);

  const { initError, ready, readySync, running, refresh } = initStore(
    async () => {
      const [inboundRequestsResponse, outboundRequestsResponse] =
        await Promise.all([
          crossbeamApi.GET('/v0.1/inbound-share-requests'),
          crossbeamApi.GET('/v0.1/outbound-share-requests'),
        ]);
      inboundShareRequests.value = inboundRequestsResponse.data?.items || [];
      outboundShareRequests.value = outboundRequestsResponse.data?.items || [];
    },
  );

  refresh({ useCache: true });

  async function refreshInboundShareRequests(): Promise<void> {
    const { data, error } = await crossbeamApi.GET(
      '/v0.1/inbound-share-requests',
    );
    if (error) {
      captureException(error);
      return;
    }
    inboundShareRequests.value = data?.items || [];
  }

  function getOutboundShareRequestById(
    id: string,
  ): OutboundShareRequest | undefined {
    return outboundShareRequests.value.find((request) => request.id === id);
  }

  function getOutboundShareRequestByPartnerAndPop(
    partnerOrgId: number,
    partnerPopId: number,
  ): OutboundShareRequest | undefined {
    return outboundShareRequests.value
      .filter(
        (request) =>
          request.partner_org_id === partnerOrgId &&
          request.partner_population_id === partnerPopId,
      )
      .at(-1);
  }

  function getInboundShareRequestById(
    id: string,
  ): InboundShareRequest | undefined {
    return inboundShareRequests.value.find((request) => request.id === id);
  }

  function getInboundShareRequestByPartnerAndPop(
    partnerOrgId: number,
    populationId: number,
  ): InboundShareRequest | undefined {
    return inboundShareRequests.value
      .filter(
        (request) =>
          request.partner_org_id === partnerOrgId &&
          request.population_id === populationId,
      )
      .at(-1);
  }

  async function ignoreShareRequest(requestId: string): Promise<void> {
    const { data } = await crossbeamApi.PATCH(
      '/v0.1/inbound-share-requests/{id}',
      {
        body: {
          status: 'ignored',
        },
        params: { path: { id: requestId } },
      },
    );
    if (data) upsertInboundShareRequest(data);
  }

  function removeDataRequestByPopulationId(populationId: number): void {
    inboundShareRequests.value = inboundShareRequests.value.filter(
      (request) => request.population_id !== populationId,
    );
  }

  async function revokeShareRequest(requestId: string): Promise<void> {
    const { data } = await crossbeamApi.PATCH(
      '/v0.1/outbound-share-requests/{id}',
      {
        body: {
          status: 'ignored',
        },
        params: { path: { id: requestId } },
      },
    );
    if (data) upsertOutboundShareRequest(data);
  }

  // helper functions
  function upsertInboundShareRequest(request: InboundShareRequest): void {
    const index = inboundShareRequests.value.findIndex(
      (o) => o.id === request.id,
    );
    if (index > -1) inboundShareRequests.value.splice(index, 1, request);
    else inboundShareRequests.value.push(request);
  }

  function upsertOutboundShareRequest(request: OutboundShareRequest): void {
    const index = outboundShareRequests.value.findIndex(
      (o) => o.id === request.id,
    );
    if (index > -1) outboundShareRequests.value.splice(index, 1, request);
    else outboundShareRequests.value.push(request);
  }

  async function sendShareRequestReminder(requestId: string): Promise<void> {
    const { data } = await crossbeamApi.POST(
      '/v0.1/share-requests/{id}/remind',
      {
        params: { path: { id: requestId } },
      },
    );
    if (data) upsertOutboundShareRequest(data);
  }

  return {
    error: initError,
    ready,
    readySync,
    running,
    getInboundShareRequestById,
    getInboundShareRequestByPartnerAndPop,
    getOutboundShareRequestById,
    getOutboundShareRequestByPartnerAndPop,
    ignoreShareRequest,
    inboundShareRequests,
    outboundShareRequests,
    refreshDataRequestsStore: refresh,
    removeDataRequestByPopulationId,
    revokeShareRequest,
    sendShareRequestReminder,
    refreshInboundShareRequests,
    /* exporting this to test it */
    upsertInboundShareRequest,
  };
});
